import { db as firebaseDb } from '../config/firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  writeBatch,
} from 'firebase/firestore';
import {
  FunctionalLocation,
  FlocType,
  FlocOperationalStatus,
  Criticality,
} from '@easydone/shared';
import { canBeChildOf } from './flocHierarchyRules';

/**
 * Remove undefined values from an object (Firebase doesn't accept undefined)
 */
function removeUndefined(obj: any): any {
  const result: any = {};
  Object.keys(obj).forEach(key => {
    const value = obj[key];
    if (value !== undefined) {
      result[key] = value;
    }
  });
  return result;
}

/**
 * Get all descendants of a FLOC (recursively)
 * Uses the path array to efficiently find all descendants
 */
export async function getAllDescendants(
  orgId: string,
  flocId: string
): Promise<FunctionalLocation[]> {
  const flocsRef = collection(firebaseDb, 'organizations', orgId, 'functionalLocations');
  const q = query(flocsRef, where('path', 'array-contains', flocId));
  const snapshot = await getDocs(q);

  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  } as FunctionalLocation));
}

/**
 * Get all functional locations for an organization
 */
export async function getFlocs(orgId: string): Promise<FunctionalLocation[]> {
  const flocsRef = collection(firebaseDb, 'organizations', orgId, 'functionalLocations');
  const q = query(flocsRef, orderBy('code', 'asc'));
  const snapshot = await getDocs(q);

  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  } as FunctionalLocation));
}

/**
 * Get functional locations by parent ID (for hierarchy navigation)
 */
export async function getFlocsByParent(
  orgId: string,
  parentFlocId: string | null
): Promise<FunctionalLocation[]> {
  const flocsRef = collection(firebaseDb, 'organizations', orgId, 'functionalLocations');
  const q = query(
    flocsRef,
    where('parentFlocId', '==', parentFlocId),
    orderBy('code', 'asc')
  );
  const snapshot = await getDocs(q);

  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  } as FunctionalLocation));
}

/**
 * Get functional locations by type
 */
export async function getFlocsByType(
  orgId: string,
  type: FlocType
): Promise<FunctionalLocation[]> {
  const flocsRef = collection(firebaseDb, 'organizations', orgId, 'functionalLocations');
  const q = query(
    flocsRef,
    where('type', '==', type),
    orderBy('name', 'asc')
  );
  const snapshot = await getDocs(q);

  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  } as FunctionalLocation));
}

/**
 * Get a single functional location
 */
export async function getFloc(
  orgId: string,
  flocId: string
): Promise<FunctionalLocation | null> {
  const flocRef = doc(firebaseDb, 'organizations', orgId, 'functionalLocations', flocId);
  const snapshot = await getDoc(flocRef);

  if (!snapshot.exists()) {
    return null;
  }

  return {
    id: snapshot.id,
    ...snapshot.data(),
  } as FunctionalLocation;
}

/**
 * Get FLOC hierarchy path (from root to current)
 */
export async function getFlocPath(
  orgId: string,
  flocId: string
): Promise<FunctionalLocation[]> {
  const floc = await getFloc(orgId, flocId);
  if (!floc) return [];

  const path: FunctionalLocation[] = [floc];
  let currentFlocId = floc.parentFlocId;

  while (currentFlocId) {
    const parentFloc = await getFloc(orgId, currentFlocId);
    if (!parentFloc) break;
    path.unshift(parentFloc);
    currentFlocId = parentFloc.parentFlocId;
  }

  return path;
}

/**
 * Create a new functional location
 */
export async function createFloc(
  orgId: string,
  data: {
    code: string;
    name: string;
    description?: string;
    parentFlocId?: string | null;
    type: FlocType;
    functionalSystem?: string;
    criticality: Criticality;
    criticalityJustification?: string;
    requiredAssetType?: string;
    technicalSpecs?: Record<string, any>;
    location?: any;
    address?: string;
    qrCode?: string;
  },
  userId: string
): Promise<FunctionalLocation> {
  // Calculate level and path
  let level = 0;
  let path: string[] = [];

  if (data.parentFlocId) {
    const parent = await getFloc(orgId, data.parentFlocId);
    if (parent) {
      level = parent.level + 1;
      path = [...parent.path, parent.id];
    }
  }

  const flocsRef = collection(firebaseDb, 'organizations', orgId, 'functionalLocations');
  const flocData = {
    orgId,
    code: data.code,
    name: data.name,
    description: data.description || null,
    parentFlocId: data.parentFlocId || null,
    level,
    path,
    childCount: 0,
    type: data.type,
    functionalSystem: data.functionalSystem || null,
    criticality: data.criticality,
    criticalityJustification: data.criticalityJustification || null,
    installedAssetId: null,
    operationalStatus: 'vacant' as FlocOperationalStatus,
    requiredAssetType: data.requiredAssetType || null,
    technicalSpecs: data.technicalSpecs || null,
    location: data.location || null,
    address: data.address || null,
    qrCode: data.qrCode || null,
    metadata: {},
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    createdBy: userId,
    updatedBy: userId,
  };

  const docRef = await addDoc(flocsRef, flocData);

  // Update parent's child count
  if (data.parentFlocId) {
    const parentRef = doc(firebaseDb, 'organizations', orgId, 'functionalLocations', data.parentFlocId);
    const parentSnap = await getDoc(parentRef);
    if (parentSnap.exists()) {
      const currentChildCount = parentSnap.data().childCount || 0;
      await updateDoc(parentRef, {
        childCount: currentChildCount + 1,
        updatedAt: serverTimestamp(),
      });
    }
  }

  const snapshot = await getDoc(docRef);
  return {
    id: snapshot.id,
    ...snapshot.data(),
  } as FunctionalLocation;
}

/**
 * Update a functional location
 */
export async function updateFloc(
  orgId: string,
  flocId: string,
  data: Partial<FunctionalLocation>,
  userId: string
): Promise<void> {
  const currentFloc = await getFloc(orgId, flocId);
  if (!currentFloc) {
    throw new Error('Functional location not found');
  }

  // Check if parent is changing
  const parentChanging = 'parentFlocId' in data && data.parentFlocId !== currentFloc.parentFlocId;

  if (parentChanging) {
    // Validate: cannot set self or descendant as parent
    if (data.parentFlocId === flocId) {
      throw new Error('Cannot set FLOC as its own parent');
    }
    if (data.parentFlocId && currentFloc.path.includes(data.parentFlocId)) {
      throw new Error('Cannot set a descendant as parent');
    }

    // Calculate new level and path
    let newLevel = 0;
    let newPath: string[] = [];

    if (data.parentFlocId) {
      const newParent = await getFloc(orgId, data.parentFlocId);
      if (!newParent) {
        throw new Error('Parent FLOC not found');
      }
      newLevel = newParent.level + 1;
      newPath = [...newParent.path, newParent.id];
    }

    // Update old parent's child count
    if (currentFloc.parentFlocId) {
      const oldParentRef = doc(firebaseDb, 'organizations', orgId, 'functionalLocations', currentFloc.parentFlocId);
      const oldParentSnap = await getDoc(oldParentRef);
      if (oldParentSnap.exists()) {
        const currentChildCount = oldParentSnap.data().childCount || 0;
        await updateDoc(oldParentRef, {
          childCount: Math.max(0, currentChildCount - 1),
          updatedAt: serverTimestamp(),
        });
      }
    }

    // Update new parent's child count
    if (data.parentFlocId) {
      const newParentRef = doc(firebaseDb, 'organizations', orgId, 'functionalLocations', data.parentFlocId);
      const newParentSnap = await getDoc(newParentRef);
      if (newParentSnap.exists()) {
        const currentChildCount = newParentSnap.data().childCount || 0;
        await updateDoc(newParentRef, {
          childCount: currentChildCount + 1,
          updatedAt: serverTimestamp(),
        });
      }
    }

    // Update the FLOC with new level and path
    const flocRef = doc(firebaseDb, 'organizations', orgId, 'functionalLocations', flocId);

    // Merge data and remove undefined values (Firebase doesn't accept undefined)
    const updateData = removeUndefined({
      ...data,
      level: newLevel,
      path: newPath,
      updatedAt: serverTimestamp(),
      updatedBy: userId,
    });

    await updateDoc(flocRef, updateData);

    // Check if FLOC has descendants - if so, recommend using updateFlocWithDescendants()
    const descendants = await getAllDescendants(orgId, flocId);
    if (descendants.length > 0) {
      console.warn(
        `FLOC ${flocId} has ${descendants.length} descendant(s). ` +
        'Consider using updateFlocWithDescendants() to properly update all descendants.'
      );
    }
  } else {
    // Simple update without parent change
    const flocRef = doc(firebaseDb, 'organizations', orgId, 'functionalLocations', flocId);

    // Merge data and remove undefined values (Firebase doesn't accept undefined)
    const updateData = removeUndefined({
      ...data,
      updatedAt: serverTimestamp(),
      updatedBy: userId,
    });

    await updateDoc(flocRef, updateData);
  }
}

/**
 * Move a FLOC to a new parent and recursively update all descendants
 * Uses Firestore batched writes for atomic updates
 */
export async function updateFlocWithDescendants(
  orgId: string,
  flocId: string,
  newParentFlocId: string | null,
  userId: string
): Promise<{
  success: boolean;
  affectedCount: number;
  error?: string;
}> {
  try {
    // 1. Validate inputs
    if (newParentFlocId === flocId) {
      return { success: false, affectedCount: 0, error: 'Cannot set FLOC as its own parent' };
    }

    // 2. Fetch current FLOC
    const currentFloc = await getFloc(orgId, flocId);
    if (!currentFloc) {
      return { success: false, affectedCount: 0, error: 'FLOC not found' };
    }

    // Prevent circular reference
    if (newParentFlocId && currentFloc.path.includes(newParentFlocId)) {
      return { success: false, affectedCount: 0, error: 'Cannot set a descendant as parent (circular reference)' };
    }

    // 3. Fetch new parent and calculate new level/path
    let newParent: FunctionalLocation | null = null;
    let newLevel = 0;
    let newPath: string[] = [];

    if (newParentFlocId) {
      newParent = await getFloc(orgId, newParentFlocId);
      if (!newParent) {
        return { success: false, affectedCount: 0, error: 'Target parent FLOC not found' };
      }
      newLevel = newParent.level + 1;
      newPath = [...newParent.path, newParent.id];

      // Validate type hierarchy
      const typeValidation = canBeChildOf(currentFloc.type, newParent.type);
      if (!typeValidation.valid) {
        return { success: false, affectedCount: 0, error: typeValidation.reason || 'Invalid type hierarchy' };
      }
    } else {
      // Moving to root level
      const typeValidation = canBeChildOf(currentFloc.type, null);
      if (!typeValidation.valid) {
        return { success: false, affectedCount: 0, error: typeValidation.reason || 'Invalid type hierarchy' };
      }
    }

    // 4. Collect all descendants
    const descendants = await getAllDescendants(orgId, flocId);

    // 5. Get old parent for childCount update
    let oldParent: FunctionalLocation | null = null;
    if (currentFloc.parentFlocId) {
      oldParent = await getFloc(orgId, currentFloc.parentFlocId);
    }

    // 6. Calculate level delta for descendants
    const levelDelta = newLevel - currentFloc.level;

    // 7. Create batch operations
    const batch = writeBatch(firebaseDb);
    let operationCount = 0;

    // Update moved FLOC
    const flocRef = doc(firebaseDb, 'organizations', orgId, 'functionalLocations', flocId);
    batch.update(flocRef, {
      parentFlocId: newParentFlocId,
      level: newLevel,
      path: newPath,
      updatedAt: serverTimestamp(),
      updatedBy: userId,
    });
    operationCount++;

    // Update all descendants
    for (const descendant of descendants) {
      // Calculate new path for descendant
      // Remove the old ancestor path up to and including the moved FLOC
      const indexInPath = descendant.path.indexOf(flocId);
      const remainingPath = descendant.path.slice(indexInPath + 1);

      const descendantNewPath = [
        ...newPath,      // New ancestor path
        flocId,          // The moved FLOC
        ...remainingPath // Rest of original path
      ];

      const descendantRef = doc(firebaseDb, 'organizations', orgId, 'functionalLocations', descendant.id);
      batch.update(descendantRef, {
        level: descendant.level + levelDelta,
        path: descendantNewPath,
        updatedAt: serverTimestamp(),
      });
      operationCount++;
    }

    // Update old parent childCount
    if (oldParent) {
      const oldParentRef = doc(firebaseDb, 'organizations', orgId, 'functionalLocations', currentFloc.parentFlocId!);
      batch.update(oldParentRef, {
        childCount: Math.max(0, (oldParent.childCount || 1) - 1),
        updatedAt: serverTimestamp(),
      });
      operationCount++;
    }

    // Update new parent childCount
    if (newParent) {
      const newParentRef = doc(firebaseDb, 'organizations', orgId, 'functionalLocations', newParentFlocId!);
      batch.update(newParentRef, {
        childCount: (newParent.childCount || 0) + 1,
        updatedAt: serverTimestamp(),
      });
      operationCount++;
    }

    // 8. Handle batches (Firestore has 500 operation limit)
    if (operationCount > 500) {
      return {
        success: false,
        affectedCount: 0,
        error: `Too many operations (${operationCount}). Please contact support for large hierarchy moves.`
      };
    }

    // 9. Commit batch
    await batch.commit();

    return {
      success: true,
      affectedCount: descendants.length + 1, // +1 for the moved FLOC itself
    };

  } catch (error: any) {
    console.error('Failed to move FLOC with descendants:', error);
    return {
      success: false,
      affectedCount: 0,
      error: error.message || 'Unknown error occurred during move'
    };
  }
}

/**
 * Delete a functional location
 * Only allowed if no children and no installed asset
 */
export async function deleteFloc(
  orgId: string,
  flocId: string
): Promise<void> {
  const floc = await getFloc(orgId, flocId);
  if (!floc) {
    throw new Error('Functional location not found');
  }

  if (floc.childCount && floc.childCount > 0) {
    throw new Error('Cannot delete functional location with child locations');
  }

  if (floc.installedAssetId) {
    throw new Error('Cannot delete functional location with installed asset');
  }

  // Update parent's child count
  if (floc.parentFlocId) {
    const parentRef = doc(firebaseDb, 'organizations', orgId, 'functionalLocations', floc.parentFlocId);
    const parentSnap = await getDoc(parentRef);
    if (parentSnap.exists()) {
      const currentChildCount = parentSnap.data().childCount || 0;
      await updateDoc(parentRef, {
        childCount: Math.max(0, currentChildCount - 1),
        updatedAt: serverTimestamp(),
      });
    }
  }

  const flocRef = doc(firebaseDb, 'organizations', orgId, 'functionalLocations', flocId);
  await deleteDoc(flocRef);
}

/**
 * Delete FLOC and all its descendants (cascading delete)
 */
export async function deleteFlocWithDescendants(
  orgId: string,
  flocId: string
): Promise<{ deleted: number; errors: string[] }> {
  const floc = await getFloc(orgId, flocId);
  if (!floc) {
    throw new Error('Functional location not found');
  }

  // Check if any FLOC in the tree has an installed asset
  const descendants = await getAllDescendants(orgId, flocId);
  const allFlocs = [floc, ...descendants];

  const flocsWithAssets = allFlocs.filter(f => f.installedAssetId);
  if (flocsWithAssets.length > 0) {
    throw new Error(
      `Cannot delete: ${flocsWithAssets.length} location(s) have installed assets. ` +
      'Remove assets before deleting.'
    );
  }

  const errors: string[] = [];
  let deleted = 0;

  // Delete all descendants first (bottom-up)
  const sortedFlocs = [...descendants].sort((a, b) => b.level - a.level);

  // Process in batches
  const batchSize = 500;
  for (let i = 0; i < sortedFlocs.length; i += batchSize) {
    const batch = writeBatch(firebaseDb);
    const batchFlocs = sortedFlocs.slice(i, i + batchSize);

    for (const descendant of batchFlocs) {
      try {
        const flocRef = doc(firebaseDb, 'organizations', orgId, 'functionalLocations', descendant.id);
        batch.delete(flocRef);
        deleted++;
      } catch (err: any) {
        errors.push(`Failed to delete ${descendant.code}: ${err.message}`);
      }
    }

    await batch.commit();
  }

  // Finally delete the parent FLOC itself
  try {
    const flocRef = doc(firebaseDb, 'organizations', orgId, 'functionalLocations', flocId);
    await deleteDoc(flocRef);
    deleted++;

    // Update parent's child count
    if (floc.parentFlocId) {
      const parentRef = doc(firebaseDb, 'organizations', orgId, 'functionalLocations', floc.parentFlocId);
      const parentSnap = await getDoc(parentRef);
      if (parentSnap.exists()) {
        const currentChildCount = parentSnap.data().childCount || 0;
        await updateDoc(parentRef, {
          childCount: Math.max(0, currentChildCount - 1),
          updatedAt: serverTimestamp(),
        });
      }
    }
  } catch (err: any) {
    errors.push(`Failed to delete ${floc.code}: ${err.message}`);
  }

  return { deleted, errors };
}

/**
 * Update FLOC operational status
 */
export async function updateFlocStatus(
  orgId: string,
  flocId: string,
  status: FlocOperationalStatus,
  userId: string
): Promise<void> {
  const flocRef = doc(firebaseDb, 'organizations', orgId, 'functionalLocations', flocId);
  await updateDoc(flocRef, {
    operationalStatus: status,
    updatedAt: serverTimestamp(),
    updatedBy: userId,
  });
}

/**
 * Toggle hidden status of a FLOC
 */
export async function toggleFlocHidden(
  orgId: string,
  flocId: string,
  hidden: boolean,
  userId: string
): Promise<void> {
  const flocRef = doc(firebaseDb, 'organizations', orgId, 'functionalLocations', flocId);
  await updateDoc(flocRef, {
    hidden,
    updatedAt: serverTimestamp(),
    updatedBy: userId,
  });
}

/**
 * Toggle hidden status of a FLOC and all its descendants
 */
export async function toggleFlocHiddenWithDescendants(
  orgId: string,
  flocId: string,
  hidden: boolean,
  userId: string
): Promise<{ success: boolean; affectedCount: number; error?: string }> {
  try {
    // Get all descendants
    const descendants = await getAllDescendants(orgId, flocId);

    // Prepare batch updates
    const updates: Promise<void>[] = [];

    // Update the parent FLOC
    const parentRef = doc(firebaseDb, 'organizations', orgId, 'functionalLocations', flocId);
    updates.push(
      updateDoc(parentRef, {
        hidden,
        updatedAt: serverTimestamp(),
        updatedBy: userId,
      })
    );

    // Update all descendants
    descendants.forEach(descendant => {
      const descendantRef = doc(firebaseDb, 'organizations', orgId, 'functionalLocations', descendant.id);
      updates.push(
        updateDoc(descendantRef, {
          hidden,
          updatedAt: serverTimestamp(),
          updatedBy: userId,
        })
      );
    });

    // Execute all updates
    await Promise.all(updates);

    return {
      success: true,
      affectedCount: descendants.length + 1, // +1 for the parent
    };
  } catch (error: any) {
    console.error('Failed to toggle hidden status with descendants:', error);
    return {
      success: false,
      affectedCount: 0,
      error: error.message || 'Unknown error',
    };
  }
}

/**
 * Assign asset to FLOC
 */
export async function assignAssetToFloc(
  orgId: string,
  flocId: string,
  assetId: string,
  userId: string
): Promise<void> {
  const flocRef = doc(firebaseDb, 'organizations', orgId, 'functionalLocations', flocId);
  await updateDoc(flocRef, {
    installedAssetId: assetId,
    operationalStatus: 'operational',
    updatedAt: serverTimestamp(),
    updatedBy: userId,
  });
}

/**
 * Remove asset from FLOC
 */
export async function removeAssetFromFloc(
  orgId: string,
  flocId: string,
  userId: string
): Promise<void> {
  const flocRef = doc(firebaseDb, 'organizations', orgId, 'functionalLocations', flocId);
  await updateDoc(flocRef, {
    installedAssetId: null,
    operationalStatus: 'vacant',
    updatedAt: serverTimestamp(),
    updatedBy: userId,
  });
}

/**
 * Get root-level FLOCs (no parent)
 */
export async function getRootFlocs(orgId: string): Promise<FunctionalLocation[]> {
  return getFlocsByParent(orgId, null);
}

/**
 * Search FLOCs by code or name
 */
export async function searchFlocs(
  orgId: string,
  searchTerm: string
): Promise<FunctionalLocation[]> {
  // Note: This is a simple client-side search
  // For production, consider using Algolia or similar for better search
  const allFlocs = await getFlocs(orgId);
  const searchLower = searchTerm.toLowerCase();

  return allFlocs.filter(
    floc =>
      floc.code.toLowerCase().includes(searchLower) ||
      floc.name.toLowerCase().includes(searchLower) ||
      floc.description?.toLowerCase().includes(searchLower)
  );
}
